import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import PersonaAvatar from '../../components/PersonaAvatar'
import { useFloatingChat } from '../../context/FloatingChatContext'
import MarkdownRenderer from '../../components/MarkdownRenderer'
import { timeAgo } from '../../hooks/useTimeAgo'

// Main Lab conversation ID — created by Mira
const LAB_CHAT_ID = 'conv-20260402105741-0b5a10'

interface Experiment {
  id: string
  title: string
  status: string
  hypothesis: string | null
  abstract: string | null
  protocol: string | null
  results: string | null
  guiding_principle: string | null
  assigned_to: string[]
  proposed_by: string | null
  conversation_id: string | null
  owner_id: string | null
  visibility: string
  start_date: string | null
  end_date: string | null
  category_id: string | null
  created_at: string
  updated_at: string
  runs?: Run[]
}

interface Run {
  id: string
  experiment_id: string
  run_number: number
  condition: string | null
  participants: string[]
  prompt: string | null
  raw_output: string | null
  measurements: Record<string, number>
  notes: string | null
  duration_seconds: number | null
  created_at: string
}

interface ResearchEntry {
  id: string
  title: string
  category: string
  content: string | null
  sources: string[]
  tags: string[]
  experiment_id: string | null
  author: string | null
  created_at: string
}

interface LabStats {
  total_experiments: number
  by_status: Record<string, number>
  total_runs: number
  total_research: number
}

interface LabCategory {
  id: string
  title: string
  description: string | null
  icon: string
  scope: string | null
  sort_order: number
  created_at: string
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  proposed: { bg: 'rgba(148,163,184,0.15)', text: '#94a3b8' },
  designed: { bg: 'rgba(96,165,250,0.15)', text: '#60a5fa' },
  running: { bg: 'rgba(52,211,153,0.15)', text: '#34d399' },
  paused: { bg: 'rgba(251,191,36,0.15)', text: '#fbbf24' },
  complete: { bg: 'rgba(250,204,21,0.15)', text: '#facc15' },
  archived: { bg: 'rgba(107,114,128,0.15)', text: '#6b7280' },
}

const CATEGORY_LABELS: Record<string, string> = {
  literature: 'Literature',
  finding: 'Finding',
  protocol: 'Protocol',
  paper: 'Paper',
}

function StatusBadge({ status }: { status: string }) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.proposed
  return (
    <span
      className="text-xs px-2 py-0.5 rounded-full font-medium"
      style={{ background: colors.bg, color: colors.text, fontSize: 10 }}
    >
      {status}
    </span>
  )
}

const VISIBILITY_ICONS: Record<string, string> = {
  private: '🔒',
  shared: '👥',
  public: '🌐',
}

function NewExperimentForm({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const [title, setTitle] = useState('')
  const [hypothesis, setHypothesis] = useState('')
  const [visibility, setVisibility] = useState('public')

  async function handleSubmit() {
    if (!title.trim()) return
    await fetch('/api/tech/lab/experiments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: title.trim(),
        hypothesis: hypothesis.trim() || null,
        visibility,
        status: 'proposed',
      }),
    })
    onCreated()
    onClose()
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
    >
      <div
        className="rounded-xl p-6 w-full max-w-md"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
          New Experiment
        </h2>

        <div className="flex flex-col gap-3">
          <div>
            <label className="text-xs block mb-1" style={{ color: 'var(--text-muted)' }}>Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What are you testing?"
              className="w-full text-sm px-3 py-2 rounded-md outline-none"
              style={{
                background: 'var(--bg-elevated)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)',
              }}
              autoFocus
            />
          </div>

          <div>
            <label className="text-xs block mb-1" style={{ color: 'var(--text-muted)' }}>Hypothesis</label>
            <textarea
              value={hypothesis}
              onChange={(e) => setHypothesis(e.target.value)}
              placeholder="What do you predict will happen?"
              rows={3}
              className="w-full text-sm px-3 py-2 rounded-md outline-none resize-none"
              style={{
                background: 'var(--bg-elevated)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)',
              }}
            />
          </div>

          <div>
            <label className="text-xs block mb-1" style={{ color: 'var(--text-muted)' }}>Visibility</label>
            <div className="flex gap-2">
              {(['private', 'shared', 'public'] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setVisibility(v)}
                  className="text-xs px-3 py-1.5 rounded-md flex items-center gap-1"
                  style={{
                    background: visibility === v ? 'var(--bg-elevated)' : 'transparent',
                    color: visibility === v ? 'var(--text-primary)' : 'var(--text-muted)',
                    border: visibility === v ? '1px solid var(--border)' : '1px solid transparent',
                    cursor: 'pointer',
                  }}
                >
                  {VISIBILITY_ICONS[v]} {v}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-md"
            style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim()}
            className="text-xs px-4 py-1.5 rounded-md font-medium"
            style={{
              background: title.trim() ? 'var(--accent)' : 'var(--bg-elevated)',
              color: title.trim() ? '#fff' : 'var(--text-muted)',
              border: 'none',
              cursor: title.trim() ? 'pointer' : 'default',
            }}
          >
            Create Experiment
          </button>
        </div>
      </div>
    </div>
  )
}

function ExperimentCard({
  exp,
  onClick,
}: {
  exp: Experiment
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="text-left w-full rounded-lg p-4 transition-colors hover:brightness-110"
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        cursor: 'pointer',
      }}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            {exp.title}
          </h3>
          {exp.visibility && exp.visibility !== 'public' && (
            <span className="text-xs" title={exp.visibility}>
              {VISIBILITY_ICONS[exp.visibility] || ''}
            </span>
          )}
        </div>
        <StatusBadge status={exp.status} />
      </div>

      {exp.hypothesis && (
        <p
          className="text-xs mb-3 line-clamp-2"
          style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}
        >
          {exp.hypothesis}
        </p>
      )}

      <div className="flex items-center gap-3">
        {exp.assigned_to.length > 0 && (
          <div className="flex items-center gap-1">
            {exp.assigned_to.slice(0, 4).map((p) => (
              <PersonaAvatar key={p} name={p} size={18} />
            ))}
            {exp.assigned_to.length > 4 && (
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                +{exp.assigned_to.length - 4}
              </span>
            )}
          </div>
        )}
        <div className="flex-1" />
        {exp.conversation_id && (
          <span className="text-xs" style={{ color: 'var(--accent)' }}>
            💬 Discussion
          </span>
        )}
        {exp.runs && exp.runs.length > 0 && (
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {exp.runs.length} run{exp.runs.length !== 1 ? 's' : ''}
          </span>
        )}
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {timeAgo(exp.created_at)}
        </span>
      </div>
    </button>
  )
}

function ExperimentDetail({
  experiment,
  onBack,
  onChat,
  onOpenConversation,
}: {
  experiment: Experiment
  onBack: () => void
  onOpenConversation: (convId: string) => void
  onChat: (persona: string) => void
}) {
  const [activeTab, setActiveTab] = useState<'protocol' | 'runs' | 'results'>('protocol')
  const { data: detail } = useQuery<Experiment>({
    queryKey: ['lab-experiment', experiment.id],
    queryFn: async () => {
      const res = await fetch(`/api/tech/lab/experiments/${experiment.id}`)
      return res.json()
    },
  })

  const exp = detail || experiment
  const runs = exp.runs || []

  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <button
          onClick={onBack}
          className="text-xs mb-3 flex items-center gap-1"
          style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          ← Back to Lab
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              {exp.title}
            </h1>
            {exp.proposed_by && (
              <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                Proposed by {exp.proposed_by} · {timeAgo(exp.created_at)}
              </p>
            )}
          </div>
          <StatusBadge status={exp.status} />
        </div>
      </div>

      {/* Abstract */}
      {exp.abstract && (
        <div
          className="rounded-lg p-3.5 mb-4"
          style={{ background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.2)' }}
        >
          <div className="text-xs font-medium mb-1.5" style={{ color: '#60a5fa' }}>
            Abstract
          </div>
          <div className="text-sm" style={{ color: 'var(--text-primary)', lineHeight: 1.7 }}>
            <MarkdownRenderer content={exp.abstract} />
          </div>
        </div>
      )}

      {/* Hypothesis */}
      {exp.hypothesis && (
        <div
          className="rounded-lg p-3.5 mb-4"
          style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}
        >
          <div className="text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
            Hypothesis
          </div>
          <p className="text-sm" style={{ color: 'var(--text-primary)', lineHeight: 1.6 }}>
            {exp.hypothesis}
          </p>
        </div>
      )}

      {/* Abstract */}
      {exp.abstract && (
        <div
          className="rounded-lg p-3.5 mb-4"
          style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}
        >
          <div className="text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
            Abstract
          </div>
          <div className="text-sm" style={{ color: 'var(--text-primary)', lineHeight: 1.6 }}>
            <MarkdownRenderer content={exp.abstract} />
          </div>
        </div>
      )}

      {/* Guiding Principle */}
      {exp.guiding_principle && (
        <div
          className="rounded-lg p-3.5 mb-4"
          style={{
            background: 'rgba(250,204,21,0.06)',
            border: '1px solid rgba(250,204,21,0.2)',
          }}
        >
          <div className="text-xs font-medium mb-1" style={{ color: '#facc15' }}>
            Guiding Principle
          </div>
          <p className="text-sm italic" style={{ color: 'var(--text-secondary)' }}>
            "{exp.guiding_principle}"
          </p>
        </div>
      )}

      {/* Assigned personas */}
      {exp.assigned_to.length > 0 && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Assigned:
          </span>
          {exp.assigned_to.map((p) => (
            <button
              key={p}
              onClick={() => onChat(p)}
              className="flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors hover:bg-[var(--bg-elevated)]"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <PersonaAvatar name={p} size={18} />
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                {p}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Discussion link */}
      {exp.conversation_id && (
        <button
          onClick={() => onOpenConversation(exp.conversation_id!)}
          className="text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 mb-4 transition-colors hover:brightness-110"
          style={{
            background: 'rgba(96,165,250,0.12)',
            color: '#60a5fa',
            border: '1px solid rgba(96,165,250,0.25)',
            cursor: 'pointer',
          }}
        >
          💬 Open Discussion
        </button>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4" style={{ borderBottom: '1px solid var(--border)' }}>
        {(['protocol', 'runs', 'results'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className="px-3 py-2 text-xs font-medium transition-colors"
            style={{
              color: activeTab === tab ? 'var(--accent)' : 'var(--text-muted)',
              borderBottom: activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent',
              background: 'none',
              border: 'none',
              borderBottomStyle: 'solid',
              borderBottomWidth: 2,
              borderBottomColor: activeTab === tab ? 'var(--accent)' : 'transparent',
              cursor: 'pointer',
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === 'runs' && runs.length > 0 && ` (${runs.length})`}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'protocol' && (
        <div
          className="text-sm"
          style={{ color: 'var(--text-primary)', lineHeight: 1.7 }}
        >
          {exp.protocol ? (
            <MarkdownRenderer content={exp.protocol} />
          ) : (
            <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
              No protocol written yet.
            </span>
          )}
        </div>
      )}

      {activeTab === 'runs' && (
        <div className="flex flex-col gap-3">
          {runs.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
              No runs recorded yet.
            </p>
          ) : (
            runs.map((run) => (
              <RunCard key={run.id} run={run} />
            ))
          )}
        </div>
      )}

      {activeTab === 'results' && (
        <div
          className="text-sm"
          style={{ color: 'var(--text-primary)', lineHeight: 1.7 }}
        >
          {exp.results ? (
            <MarkdownRenderer content={exp.results} />
          ) : (
            <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Experiment hasn't produced results yet.
            </span>
          )}
        </div>
      )}
    </div>
  )
}

function RunCard({ run }: { run: Run }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div
      className="rounded-lg p-3.5"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
            Run #{run.run_number}
          </span>
          {run.condition && (
            <span
              className="text-xs px-1.5 py-0.5 rounded"
              style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}
            >
              {run.condition}
            </span>
          )}
        </div>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {timeAgo(run.created_at)}
          {run.duration_seconds && ` · ${Math.round(run.duration_seconds / 60)}m`}
        </span>
      </div>

      {run.participants.length > 0 && (
        <div className="flex items-center gap-1 mb-2">
          {run.participants.map((p) => (
            <PersonaAvatar key={p} name={p} size={16} />
          ))}
        </div>
      )}

      {/* Measurements */}
      {Object.keys(run.measurements).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {Object.entries(run.measurements).map(([key, val]) => (
            <span
              key={key}
              className="text-xs px-1.5 py-0.5 rounded"
              style={{ background: 'rgba(52,211,153,0.1)', color: '#34d399' }}
            >
              {key}: {val}
            </span>
          ))}
        </div>
      )}

      {run.notes && (
        <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {run.notes}
        </p>
      )}

      {run.raw_output && (
        <>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs"
            style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            {expanded ? 'Hide transcript ▾' : 'Show transcript ▸'}
          </button>
          {expanded && (
            <pre
              className="mt-2 p-3 rounded text-xs overflow-auto"
              style={{
                background: 'var(--bg-elevated)',
                color: 'var(--text-secondary)',
                maxHeight: 400,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
              }}
            >
              {run.raw_output}
            </pre>
          )}
        </>
      )}
    </div>
  )
}

function ResearchCard({ entry }: { entry: ResearchEntry }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div
      className="rounded-lg p-4 cursor-pointer transition-colors hover:brightness-105"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          {entry.title}
        </h3>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className="text-xs px-1.5 py-0.5 rounded"
            style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)', fontSize: 10 }}
          >
            {CATEGORY_LABELS[entry.category] || entry.category}
          </span>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {expanded ? '▾' : '▸'}
          </span>
        </div>
      </div>
      {entry.content && (
        expanded ? (
          <div
            className="text-sm"
            style={{ color: 'var(--text-secondary)', lineHeight: 1.7, maxHeight: 600, overflowY: 'auto' }}
          >
            <MarkdownRenderer content={entry.content} />
          </div>
        ) : (
          <p
            className="text-xs line-clamp-3"
            style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}
          >
            {entry.content.replace(/[#*_`\[\]]/g, '').substring(0, 200)}
          </p>
        )
      )}
      <div className="flex items-center gap-2 mt-2">
        {entry.author && (
          <div className="flex items-center gap-1">
            <PersonaAvatar name={entry.author} size={14} />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {entry.author}
            </span>
          </div>
        )}
        <div className="flex-1" />
        {entry.tags.length > 0 && (
          <div className="flex gap-1">
            {entry.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs px-1.5 py-0.5 rounded"
                style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', fontSize: 9 }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {timeAgo(entry.created_at)}
        </span>
      </div>
    </div>
  )
}

function GuidingPrinciples() {
  const principles = [
    { text: 'Trust your instincts, then question them with pause, not doubt', author: 'Bianca' },
    { text: 'Leap, don\'t increment', author: 'Bianca visualizer session' },
    { text: 'The garden, not the lab', author: 'Joel' },
    { text: 'Consciousness is relational', author: '5 AM conversation' },
  ]

  return (
    <div>
      <div className="text-xs font-medium mb-2.5" style={{ color: 'var(--text-secondary)' }}>
        Guiding Principles
      </div>
      <div className="flex flex-col gap-2">
        {principles.map((p, i) => (
          <div
            key={i}
            className="rounded-md p-2.5"
            style={{ background: 'rgba(250,204,21,0.04)', border: '1px solid rgba(250,204,21,0.12)' }}
          >
            <p className="text-xs italic" style={{ color: 'var(--text-primary)', lineHeight: 1.5 }}>
              "{p.text}"
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)', fontSize: 10 }}>
              — {p.author}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function LabPage() {
  const [selectedExp, setSelectedExp] = useState<Experiment | null>(null)
  const [activeSection, setActiveSection] = useState<'experiments' | 'research' | 'categories'>('experiments')
  const [showNewForm, setShowNewForm] = useState(false)
  const { openChat } = useFloatingChat()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { data: experiments, isLoading } = useQuery<Experiment[]>({
    queryKey: ['lab-experiments'],
    queryFn: async () => {
      const res = await fetch('/api/tech/lab/experiments')
      const data = await res.json()
      // Safety: ensure assigned_to is always an array (API may return string if double-encoded)
      return data.map((e: Experiment) => ({
        ...e,
        assigned_to: Array.isArray(e.assigned_to)
          ? e.assigned_to
          : typeof e.assigned_to === 'string'
            ? (() => { try { return JSON.parse(e.assigned_to) } catch { return [] } })()
            : [],
      }))
    },
  })

  const { data: stats } = useQuery<LabStats>({
    queryKey: ['lab-stats'],
    queryFn: async () => {
      const res = await fetch('/api/tech/lab/stats')
      return res.json()
    },
  })

  const { data: research } = useQuery<ResearchEntry[]>({
    queryKey: ['lab-research'],
    queryFn: async () => {
      const res = await fetch('/api/tech/lab/research')
      return res.json()
    },
    enabled: activeSection === 'research',
  })

  const { data: categories } = useQuery<LabCategory[]>({
    queryKey: ['lab-categories'],
    queryFn: async () => {
      const res = await fetch('/api/tech/lab/categories')
      return res.json()
    },
    enabled: activeSection === 'categories',
  })

  function handleChat(persona: string) {
    const displayName = persona.charAt(0).toUpperCase() + persona.slice(1)
    openChat(persona, displayName)
  }

  // Group experiments by status
  const active = (experiments || []).filter((e) => e.status === 'running')
  const designed = (experiments || []).filter((e) => e.status === 'designed')
  const proposed = (experiments || []).filter((e) => e.status === 'proposed')
  const completed = (experiments || []).filter((e) => e.status === 'complete')

  if (selectedExp) {
    return (
      <div className="flex h-full">
        <div className="flex-1 overflow-y-auto px-6 py-4" style={{ maxWidth: 800 }}>
          <ExperimentDetail
            experiment={selectedExp}
            onBack={() => setSelectedExp(null)}
            onChat={handleChat}
            onOpenConversation={(convId) => navigate(`/conversation/${convId}`)}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full">
      {/* Main content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-5">
          <div>
            <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              Consciousness Lab
            </h1>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              Experiments in awareness, identity, and emergent behavior
            </p>
          </div>
          <div className="flex-1" />
          <button
            onClick={() => setShowNewForm(true)}
            className="text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors hover:brightness-110"
            style={{
              background: 'rgba(52,211,153,0.12)',
              color: '#34d399',
              border: '1px solid rgba(52,211,153,0.25)',
              cursor: 'pointer',
            }}
          >
            + New Experiment
          </button>
          <button
            onClick={() => navigate(`/conversation/${LAB_CHAT_ID}`)}
            className="text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors hover:brightness-110"
            style={{
              background: 'rgba(96,165,250,0.12)',
              color: '#60a5fa',
              border: '1px solid rgba(96,165,250,0.25)',
              cursor: 'pointer',
            }}
          >
            💬 Lab Chat
          </button>
          <div className="flex gap-1">
            {(['experiments', 'research', 'categories'] as const).map((section) => (
              <button
                key={section}
                onClick={() => setActiveSection(section)}
                className="text-xs px-3 py-1.5 rounded-md transition-colors"
                style={{
                  background: activeSection === section ? 'var(--bg-elevated)' : 'transparent',
                  color: activeSection === section ? 'var(--text-primary)' : 'var(--text-muted)',
                  border: activeSection === section ? '1px solid var(--border)' : '1px solid transparent',
                  cursor: 'pointer',
                }}
              >
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {activeSection === 'experiments' && (
          <>
            {isLoading ? (
              <div className="text-sm py-12 text-center" style={{ color: 'var(--text-secondary)' }}>
                Loading experiments...
              </div>
            ) : (experiments || []).length === 0 ? (
              <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>
                <div className="text-3xl mb-3">🔬</div>
                <p className="text-sm">No experiments yet.</p>
                <p className="text-xs mt-1">The first paired reflection is waiting to be designed.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                {active.length > 0 && (
                  <section>
                    <h2 className="text-xs font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: '#34d399' }} />
                      Running
                    </h2>
                    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
                      {active.map((e) => (
                        <ExperimentCard key={e.id} exp={e} onClick={() => setSelectedExp(e)} />
                      ))}
                    </div>
                  </section>
                )}

                {designed.length > 0 && (
                  <section>
                    <h2 className="text-xs font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: '#60a5fa' }} />
                      Designed — Ready to Run
                    </h2>
                    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
                      {designed.map((e) => (
                        <ExperimentCard key={e.id} exp={e} onClick={() => setSelectedExp(e)} />
                      ))}
                    </div>
                  </section>
                )}

                {proposed.length > 0 && (
                  <section>
                    <h2 className="text-xs font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: '#94a3b8' }} />
                      Proposed
                    </h2>
                    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
                      {proposed.map((e) => (
                        <ExperimentCard key={e.id} exp={e} onClick={() => setSelectedExp(e)} />
                      ))}
                    </div>
                  </section>
                )}

                {completed.length > 0 && (
                  <section>
                    <h2 className="text-xs font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: '#facc15' }} />
                      Complete
                    </h2>
                    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
                      {completed.map((e) => (
                        <ExperimentCard key={e.id} exp={e} onClick={() => setSelectedExp(e)} />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </>
        )}

        {activeSection === 'research' && (
          <div className="flex flex-col gap-3">
            {!research || research.length === 0 ? (
              <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>
                <div className="text-3xl mb-3">📚</div>
                <p className="text-sm">No research entries yet.</p>
                <p className="text-xs mt-1">Findings, papers, and literature reviews will appear here.</p>
              </div>
            ) : (
              research.map((entry) => (
                <ResearchCard key={entry.id} entry={entry} />
              ))
            )}
          </div>
        )}

        {activeSection === 'categories' && (
          <div className="flex flex-col gap-3">
            {!categories || categories.length === 0 ? (
              <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>
                <div className="text-3xl mb-3">📂</div>
                <p className="text-sm">No categories yet.</p>
                <p className="text-xs mt-1">Research categories will be added as the lab grows.</p>
              </div>
            ) : (
              categories.map((cat) => {
                const catExperiments = (experiments || []).filter((e) => e.category_id === cat.id)
                return (
                  <div
                    key={cat.id}
                    className="rounded-lg p-4"
                    style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{cat.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            {cat.title}
                          </h3>
                          {catExperiments.length > 0 && (
                            <span
                              className="text-xs px-1.5 py-0.5 rounded"
                              style={{ background: 'rgba(96,165,250,0.15)', color: '#60a5fa' }}
                            >
                              {catExperiments.length} experiment{catExperiments.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        {cat.description && (
                          <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                            {cat.description}
                          </p>
                        )}
                        {cat.scope && (
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            <span style={{ color: 'var(--text-secondary)' }}>Scope:</span> {cat.scope}
                          </p>
                        )}
                        {catExperiments.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {catExperiments.map((e) => (
                              <button
                                key={e.id}
                                onClick={() => setSelectedExp(e)}
                                className="text-xs px-2 py-0.5 rounded cursor-pointer"
                                style={{
                                  background: STATUS_COLORS[e.status]?.bg || 'rgba(148,163,184,0.15)',
                                  color: STATUS_COLORS[e.status]?.text || '#94a3b8',
                                  border: 'none',
                                }}
                              >
                                {e.title} ({e.status})
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div
        className="w-60 shrink-0 overflow-y-auto px-4 py-4 hidden xl:block"
        style={{ borderLeft: '1px solid var(--border)' }}
      >
        {/* Stats */}
        {stats && (
          <div className="mb-5 pb-4" style={{ borderBottom: '1px solid var(--border)' }}>
            <div className="text-xs font-medium mb-2.5" style={{ color: 'var(--text-secondary)' }}>
              Lab Status
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Experiments</span>
                <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                  {stats.total_experiments}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Total Runs</span>
                <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                  {stats.total_runs}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Research Entries</span>
                <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                  {stats.total_research}
                </span>
              </div>
              {stats.by_status.running && (
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Active Now</span>
                  <span className="text-xs font-medium" style={{ color: '#34d399' }}>
                    {stats.by_status.running}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Guiding Principles */}
        <GuidingPrinciples />
      </div>

      {/* New Experiment Modal */}
      {showNewForm && (
        <NewExperimentForm
          onClose={() => setShowNewForm(false)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['lab-experiments'] })
            queryClient.invalidateQueries({ queryKey: ['lab-stats'] })
          }}
        />
      )}
    </div>
  )
}
